import { useState, type ComponentProps } from 'react';
import Icon from '@/components/Icon';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';

export interface PasswordInputProps extends Omit<ComponentProps<'input'>, 'type'> {
  /** `data-ee-feature` for the show/hide toggle. Every interactive element carries one. */
  revealFeature: string;
}

/**
 * A password `Input` with a show/hide toggle. The field itself stays `Input` — this only adds
 * the eye button, so it inherits every prop `Input` takes.
 */
export function PasswordInput({ className, revealFeature, ...props }: PasswordInputProps) {
  const [shown, setShown] = useState(false);

  return (
    <div className="relative">
      <Input type={shown ? 'text' : 'password'} className={cn('pr-11', className)} {...props} />
      <button
        type="button"
        onClick={() => setShown((value) => !value)}
        aria-label="Show password"
        aria-pressed={shown}
        data-ee-feature={revealFeature}
        className={cn(
          'absolute inset-y-0 right-0 flex h-11 w-11 items-center justify-center text-muted-foreground',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        )}
      >
        <Icon name={shown ? 'eye-off' : 'eye'} size={20} />
      </button>
    </div>
  );
}
